import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface PendingIssue {
  number: number;
  title: string;
  url: string;
  labels: string[];
  created_at: string;
}

export interface PullRequest {
  number: number;
  title: string;
  url: string;
  state: 'open' | 'merged' | 'closed';
  created_at: string;
  merged_at?: string;
  repo: string;
}

export interface LifecycleData {
  pending_issues: PendingIssue[];
  open_prs: PullRequest[];
  recently_merged: PullRequest[];
  counts: {
    pending: number;
    open_prs: number;
    merged_last_7d: number;
    closed: number;
  };
}

export interface GeneratedClaudeMd {
  content: string;
  repo: string;
}

/** The agent-config formats the governed corpus can be emitted to. */
export type AgentFormatId = 'claude' | 'agents' | 'skill' | 'cursor';

export interface AgentFormat {
  id: AgentFormatId;
  label: string;
  /** Which agents read this file — shown so the choice is an informed one. */
  consumers: string;
}

export interface GeneratedAgentFile {
  format: AgentFormatId;
  path: string;
  content: string;
}

export interface GeneratedAgentConfig {
  repo: string;
  files: GeneratedAgentFile[];
}

export const workflowApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async checkIssue(description: string, repo: string): Promise<{ found: boolean; issues?: { number: number; title: string; url: string }[] }> {
    const { data } = await api.post('/workflow/check', { description, repo });
    return data;
  },

  async createAndAssign(params: { title: string; description: string; repo: string; labels?: string[]; acceptanceCriteria?: string[] }) {
    const { data } = await api.post('/workflow/create-and-assign', params);
    return data;
  },

  async listPending(repo: string): Promise<PendingIssue[]> {
    const { data } = await api.get('/workflow/pending', { params: { repo } });
    return data;
  },

  async getLifecycle(repo?: string): Promise<LifecycleData> {
    const { data } = await api.get('/workflow/lifecycle', { params: repo ? { repo } : {} });
    return data;
  },

  async generateClaudeMd(repo: string, productName: string, projectUrl?: string): Promise<GeneratedClaudeMd> {
    const { data } = await api.post('/workflow/generate-claude-md', { repo, productName, projectUrl });
    return data;
  },

  async listAgentFormats(): Promise<AgentFormat[]> {
    const { data } = await api.get('/workflow/agent-formats');
    return data;
  },

  async generateAgentConfig(
    repo: string,
    productName: string,
    formats: AgentFormatId[],
    projectUrl?: string,
  ): Promise<GeneratedAgentConfig> {
    const { data } = await api.post('/workflow/generate-agent-config', {
      repo,
      productName,
      formats,
      projectUrl,
    });
    return data;
  },

  async publishClaudeMd(repo: string, content: string): Promise<{ published: boolean }> {
    const { data } = await api.post('/workflow/publish-claude-md', { repo, content });
    return data;
  },

  async publishFile(repo: string, path: string, content: string, commitMessage?: string): Promise<{ published: boolean }> {
    const { data } = await api.post('/workflow/publish-file', { repo, path, content, commitMessage });
    return data;
  },
};
